<script setup lang="ts">
import { IconLogoDesktop } from '~/components/icon'
import { ButtonIcon } from '@caks/uikit/button'
import { IconGeo, IconCart, IconProfileRound, IconSearch } from '@caks/uikit/icon'
import { useAccountStore } from '@caks/shared/store/store.account'
import { ModalFixed } from '../modal'
import { InputRadio } from '@caks/uikit/input'
import { getCitiesList } from '@caks/shared/api/user.route'
import { CitiesList } from '@caks/shared/types/types.api'
import { ElementSearch } from '.'
import { InputField } from '@caks/uikit/input'
import { ElementOrderProgress } from '.'
import { AuthGroup } from '~/components/auth'
import { onClickOutside } from '@vueuse/core'
import { ModalElement } from '~/types/components.types'

interface Emit {
  (e: 'getLocation'): void
}

const emit = defineEmits<Emit>()

const store = useAccountStore()
const router = useRouter()

const modal = ref<ModalElement>()
const cities = ref<{ id: number; name: string }[]>([])
const searchValue = ref('')
const searchBarActive = ref(false)
const isAuthVisible = ref(false)

const selectedCity = ref<string | null>(null)
const selectCityModal = ref<HTMLElement>()

onClickOutside(selectCityModal, () => {
  modal.value?.hide()
})

onMounted(async () => {
  const citiesData: CitiesList = await getCitiesList()
  cities.value = citiesData.data

  if (store.city) {
    selectedCity.value = store.city
  }
})

const setCity = (value: string) => {
  store.city = value
  localStorage.setItem('caksCity', value)
  const cityId = cities.value.find((el) => {
    return el.name === value
  })?.id
  if (cityId !== undefined) {
    localStorage.setItem('caksCityId', cityId.toString())
    store.cityId = cityId
  }
}

const openAsideModal = () => {
  store.changeAsideModalActive(true)
}

const goToProfile = () => {
  const token = localStorage.getItem('token')

  if (token) {
    router.push('/profile')
  } else {
    isAuthVisible.value = true
  }
}

const cartItemsAmount = computed(() => {
  return store.cart.reduce((acc, item) => {
    return (acc += item.amount)
  }, 0)
})

const logoClick = () => {
  if (router.currentRoute.value.name === 'Home') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    router.push('/')
  }
}

const openLocaitonModal = () => {
  store.locationModalIsOpen = true
}

watch(
  () => store.city,
  () => {
    selectedCity.value = store.city
  }
)
</script>

<template>
  <header class="header">
    <div class="header__container main-container">
      <div class="header__left">
        <div class="header__logo" @click="logoClick">
          <img src="/logo.png" alt="logo" />
        </div>
        <div class="header__search-field">
          <input-field
            v-model="searchValue"
            placeholder="Искать в САК’С"
            :start-icon="IconSearch"
            :no-icons="false"
            readonly
            @click="searchBarActive = true"
          />
        </div>
        <transition name="opacity">
          <element-search v-if="searchBarActive" @close="searchBarActive = false" />
        </transition>
      </div>
      <div v-if="$route.name !== 'ViewOrder'" class="header__right">
        <div class="header__location">
          <button-icon :icon="IconGeo" state="pill" @click="openLocaitonModal">
            {{ selectedCity }}
          </button-icon>
        </div>
        <ul class="header__icons">
          <button-icon
            v-if="!store.cart.length && !store.selectedLocation"
            :icon="IconCart"
            state="pill"
            @click="openAsideModal"
          />
          <button v-else class="header__full-cart-button" @click="openAsideModal">
            <p>{{ store.selectedLocation?.title || 'Адрес не выбран' }}</p>
            <div class="icon">
              <span>{{ cartItemsAmount }}</span>
            </div>
          </button>
          <button-icon
            :icon="IconProfileRound"
            :state="$route.name === 'ViewProfile' ? 'fill' : 'pill'"
            @click="goToProfile"
          />
        </ul>
      </div>
      <element-order-progress v-else />
    </div>
    <!-- <modal-fixed ref="modal" right="264px" top="75px">
      <div class="modal-content" ref="selectCityModal">
        <h3>Выберите город, чтобы посмотреть меню</h3>
        <div class="modal-content__radio-group">
          <input-radio
            v-model="selectedCity"
            v-for="city in cities"
            :key="city.id"
            :value="city.name"
            @update:model-value="setCity"
          >
            {{ city.name }}
          </input-radio>
        </div>
      </div>
    </modal-fixed> -->
    <auth-group v-if="isAuthVisible" @close="isAuthVisible = false" />
  </header>
</template>

<style scoped lang="scss">
.header {
  background-color: $white;
  width: 100%;
  max-width: 1336px;
  margin: auto;
  position: relative;
  height: 76px;

  &__container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
  }

  &__left {
    display: flex;
    gap: 46px;
  }

  &__right {
    display: flex;
    gap: 16px;
  }

  &__logo {
    cursor: pointer;
    width: 189px;
    height: 33px;
  }

  &__search-field {
    width: 527px;
  }

  &__location {
  }

  &__icons {
    --svg-col: #000;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon {
  }

  &__full-cart-button {
    display: flex;
    gap: 23px;
    align-items: center;
    @include h5-2;
    border-radius: 30px;
    border: 1px solid $primary-green;
    width: max-content;
    padding-left: 16px;

    p {
      max-width: 152px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .icon {
      padding: 10px 14px 10px 10px;
      border-radius: 30px;
      background-color: $primary-green;
      background-size: 35px 35px;
      width: 65px;
      height: 44px;
      color: white;
      position: relative;
      text-align: right;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      span {
        @include h4-6;
      }

      &:after {
        content: '';
        background: #0aa152 url('/assets/images/cart/cart.svg') no-repeat center;
        background-size: cover;
        width: 24px;
        height: 24px;
        position: absolute;
        left: 10px;
        top: 10px;
      }
    }
  }
}
.main-container {
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 32px;

  h3 {
    width: 384px;
    @include h2-1;
  }

  &__radio-group {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.opacity-enter-active,
.opacity-leave-active {
  transition: opacity 0.15s ease;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}
</style>
